// Render del configure panel del modo "Fotos de producto" (kind
// 'fotos-producto'): el producto SOLO, sin modelo ni poses.
//
// Secciones: Producto · Vistas (multi-select) · Fondo (blanco / escena
// descrita) · Otras opciones (formato + instrucción adicional).
//
// Es un panel propio y no un flag más de `DesignModeloPanel`: ese ya carga
// dos modos (design-modelo y design-auto) con 40 props y ~900 líneas, y las
// secciones que necesita este modo no se solapan con las suyas (no hay
// modelo, no hay poses, el fondo es otro picker). Ver
// ADR-2026-08-13-fotos-producto-panel-propio.
//
// El botón Generar NO vive acá — lo renderiza el ConfigurePanel padre, igual
// que en el resto de los kinds.

import { SkuDelProducto } from '../../../components/SkuDelProducto';
import React, { useEffect, useRef } from 'react';
import { Layers, Mountain, Plus, X } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
import {
  FondoProductoPicker,
  type FondoProductoSelection,
} from '../components/FondoProductoPicker';
import { VistaProductoPicker } from '../components/VistaProductoPicker';
import type { VistaProductoConjuntoOption } from '../vistasCatalogo';
import type { MockProductV2 } from '../mockData';

export interface FotosProductoPanelProps {
  variant: 'mobile' | 'desktop';
  /** Productos elegidos. Desde ADR-2026-08-30-fotos-producto-multi-producto
   *  pueden ser varios: se genera una imagen por producto por vista. */
  products: MockProductV2[];
  onClearProducts: () => void;
  /** Quita UN producto de la selección. Con el último, el caller vuelve a la
   *  pantalla de elegir producto (mismo destino que `onClearProducts`). */
  onRemoveProduct?: (id: string) => void;
  /** Abre el picker premarcado para sumar más productos (el
   *  `handleAddProducts` compartido con el outfit de plantillas). */
  onAddProducts?: () => void;
  /** Unión de vistas de los productos elegidos, con thumb resuelto y
   *  `productIds` (a qué productos aplica cada una). */
  vistas: VistaProductoConjuntoOption[];
  vistaIds: string[];
  onVistaIdsChange: (ids: string[]) => void;
  /** Productos desactivados por vista (checklist del badge de la grilla). */
  vistaProductosExcluidos?: Record<string, string[]>;
  /** Toggle de un producto para una vista (checklist del badge). */
  onToggleVistaProducto?: (vistaId: string, productId: string) => void;
  /** Total de imágenes de la tanda = Σ por vista de sus productos activos.
   *  Viene calculado del padre (mismo número que el pill del Generar). */
  totalImagenes?: number;
  fondoSelection: FondoProductoSelection;
  onFondoChange: (sel: FondoProductoSelection) => void;
  formatoLabel: string;
  onOpenFormat: () => void;
  otraConfig: string;
  onOtraChange: (v: string) => void;
  /** Id de la grilla de vistas — target del scroll+shake del Generar
   *  bloqueado. Lo provee el ConfigurePanel con el prefijo de la variante. */
  vistasGridId?: string;
}

export const FotosProductoPanel: React.FC<FotosProductoPanelProps> = ({
  variant,
  products,
  onClearProducts,
  onRemoveProduct,
  onAddProducts,
  vistas,
  vistaIds,
  onVistaIdsChange,
  vistaProductosExcluidos = {},
  onToggleVistaProducto,
  totalImagenes = 0,
  fondoSelection,
  onFondoChange,
  formatoLabel,
  onOpenFormat,
  otraConfig,
  onOtraChange,
  vistasGridId,
}) => {
  const isDesktop = variant === 'desktop';

  // Auto-resize del textarea "Instrucción adicional" — mismo comportamiento
  // que en DesignModeloPanel: crece hasta 6 líneas y después scrollea.
  const otraTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const ta = otraTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const cs = window.getComputedStyle(ta);
    const lineH = parseFloat(cs.lineHeight) || 20;
    const padT = parseFloat(cs.paddingTop) || 0;
    const padB = parseFloat(cs.paddingBottom) || 0;
    const maxH = lineH * 6 + padT + padB;
    const target = Math.min(ta.scrollHeight, maxH);
    ta.style.height = `${target}px`;
    ta.style.overflowY = ta.scrollHeight > maxH ? 'auto' : 'hidden';
  }, [otraConfig]);

  const productNames = products.map((p) => p.name);

  // ── Bloques compartidos entre desktop y mobile ─────────────────────────
  // Una fila por producto, cada una con su propia X. Quitar el ÚLTIMO cae en
  // `onClearProducts` (vuelve a la pantalla de elegir producto) — sin esto el
  // panel quedaría configurando la nada.
  const productoBlock = (
    <div className="flex flex-col gap-2">
      {products.map((p) => (
        <div
          key={p.id}
          className="flex items-center gap-3 bg-[#F7F7F9] rounded-2xl p-3"
        >
          <CachedImg
            src={p.imageUrl}
            alt={p.name}
            className="w-12 h-12 rounded-lg object-cover shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[14.5px] font-semibold text-brand-black/90 truncate">
              {p.name}
            </p>
            <SkuDelProducto sku={p.sku} />
          </div>
          <button
            type="button"
            onClick={() =>
              products.length > 1 && onRemoveProduct
                ? onRemoveProduct(p.id)
                : onClearProducts()
            }
            className="w-8 h-8 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-black/[0.04] transition-colors shrink-0"
            aria-label={
              products.length > 1 ? 'Quitar producto' : 'Cambiar producto'
            }
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      {onAddProducts && (
        <button
          type="button"
          onClick={onAddProducts}
          className="w-full inline-flex items-center justify-center gap-2 py-3 px-5 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/80 hover:text-brand-primary text-[14px] font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Agregar producto
        </button>
      )}
    </div>
  );

  const vistasBlock = (
    <VistaProductoPicker
      vistas={vistas}
      selectedIds={vistaIds}
      onChange={onVistaIdsChange}
      columns={isDesktop ? 4 : 2}
      gridId={vistasGridId}
      productos={products}
      excluidosPorVista={vistaProductosExcluidos}
      onToggleProducto={onToggleVistaProducto}
    />
  );

  const fondoBlock = (
    <FondoProductoPicker
      selection={fondoSelection}
      onChange={onFondoChange}
      productNames={productNames}
    />
  );

  const otrasOpcionesBlock = (
    <div className="flex flex-col gap-2.5">
      <button
        type="button"
        onClick={onOpenFormat}
        className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
      >
        <span
          className={`${
            isDesktop ? 'text-[13.5px]' : 'text-[14.5px] font-normal'
          } text-brand-black/85`}
        >
          Formato
        </span>
        <span
          className={`${
            isDesktop ? 'text-[13.5px]' : 'text-[14.5px]'
          } font-medium text-brand-black/85 truncate`}
        >
          {formatoLabel}
        </span>
      </button>
      <div className="relative">
        <textarea
          ref={otraTextareaRef}
          value={otraConfig}
          onChange={(e) => onOtraChange(e.target.value)}
          placeholder="Instrucción adicional"
          rows={isDesktop ? 3 : 2}
          className="w-full resize-none overflow-hidden px-4 pt-3 pb-11 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
        />
        <PromptRecientesButton
          tipo="instruccion"
          onPick={onOtraChange}
          titulo="Instrucciones usadas antes"
          className="absolute bottom-2.5 right-2.5"
        />
      </div>
    </div>
  );

  // Contador de vistas + aviso de consumo. Cada (producto activo, vista) es
  // una imagen: el usuario tiene que poder anticipar el gasto antes de tocar
  // Generar. `totalImagenes` viene del padre (descuenta los productos
  // desactivados por vista) — es el mismo número que cobra el pill.
  const vistasContador = (
    <div className="flex flex-col gap-1.5">
      <span className="inline-flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-100 text-brand-black/70 text-[12px] font-medium">
        {vistaIds.length}{' '}
        {vistaIds.length === 1 ? 'vista seleccionada' : 'vistas seleccionadas'}
      </span>
      {totalImagenes > 1 && (
        <p className="text-[12.5px] text-[#3C3C43]/60 leading-relaxed">
          {products.length > 1
            ? `Generamos una imagen por cada producto de cada vista: ${totalImagenes} imágenes en total. Tocá las miniaturas de una vista para elegir qué productos incluye.`
            : 'Generamos una imagen por vista.'}
        </p>
      )}
    </div>
  );

  if (isDesktop) {
    // OJO: desde ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo, DESKTOP
    // ya no monta este panel. Su paso 2 pasó a ser el panel compacto del Catálogo, y
    // Vistas, Fondo, Formato y Productos se eligen en el panel lateral
    // (ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral).
    //
    // La rama se deja en pie a propósito y no se borra a ciegas: `variant` sigue siendo
    // parte del contrato del panel y el modo tiene más de un punto de montaje. Si se
    // confirma que nadie la llama, se saca junto con el prop.
    //
    // Mismo layout que DesignModeloPanel en desktop: cada sección es una card
    // con título + descripción a la izquierda y el contenido a la derecha.
    const sectionWrap = (
      title: string,
      description: string,
      content: React.ReactNode,
      extra?: React.ReactNode,
    ) => (
      <section className="rounded-2xl bg-white border border-neutral-200 p-6 flex gap-6">
        <div className="w-[220px] shrink-0 flex flex-col">
          <div>
            <h3 className="text-[20px] font-semibold text-brand-black/90 leading-tight">
              {title}
            </h3>
            <p className="text-[12.5px] text-[#3C3C43]/60 mt-1.5 leading-relaxed">
              {description}
            </p>
          </div>
          {extra && <div className="mt-auto pt-3">{extra}</div>}
        </div>
        <div className="flex-1 min-w-0">{content}</div>
      </section>
    );

    return (
      <div className="flex flex-col gap-4">
        {sectionWrap(
          products.length > 1 ? 'Productos' : 'Producto',
          products.length > 1
            ? 'Vas a generar las fotos de estos productos.'
            : 'Vas a generar las fotos de este producto.',
          productoBlock,
        )}
        {sectionWrap(
          'Vistas',
          products.length > 1
            ? 'Desde qué ángulo se ven los productos. Elegí todas las que necesites: generamos una imagen de cada producto por cada vista.'
            : 'Desde qué ángulo se ve el producto. Elegí todas las que necesites: generamos una imagen por cada una.',
          vistasBlock,
          vistasContador,
        )}
        {sectionWrap(
          'Fondo',
          'Dónde aparece el producto. Fondo liso de color para catálogo, o describí la escena que quieras.',
          fondoBlock,
        )}
        {sectionWrap(
          'Otras opciones',
          'Definí el formato de salida y agregá indicaciones extras para afinar el resultado.',
          otrasOpcionesBlock,
        )}
      </div>
    );
  }

  // ── Mobile: bloques apilados con label arriba ──────────────────────────
  const mobileSection = (
    icon: React.ReactNode,
    label: string,
    content: React.ReactNode,
    extra?: React.ReactNode,
  ) => (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
          {icon}
          {label}
        </span>
        {extra}
      </div>
      {content}
    </section>
  );

  return (
    <div className="flex flex-col gap-4">
      {productoBlock}
      {mobileSection(
        <Layers className="w-3 h-3" />,
        'Vistas',
        // Con varios productos, el aviso de consumo va pegado a la grilla:
        // el pill del Generar muestra el total pero acá se entiende por qué.
        products.length > 1 ? (
          <div className="flex flex-col gap-2">
            {vistasBlock}
            {totalImagenes > 1 && (
              <p className="text-[12.5px] text-[#3C3C43]/60 leading-relaxed">
                Generamos una imagen por cada producto de cada vista:{' '}
                {totalImagenes} imágenes en total. Tocá las miniaturas de una
                vista para elegir qué productos incluye.
              </p>
            )}
          </div>
        ) : (
          vistasBlock
        ),
        <span className="text-[12px] font-medium text-brand-black/70">
          {vistaIds.length}{' '}
          {vistaIds.length === 1 ? 'seleccionada' : 'seleccionadas'}
        </span>,
      )}
      {mobileSection(<Mountain className="w-3 h-3" />, 'Fondo', fondoBlock)}
      {otrasOpcionesBlock}
    </div>
  );
};
